import { useState, useRef, useEffect } from 'react';
import { useAuth, useUserProfile, useDocumentDirection, useProgressStats } from '@immopersian/common';
import { Camera, Save, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile, updateProfile, uploadProfilePicture, isUpdating } = useUserProfile();
  const { data: progressStats } = useProgressStats();
  const [fullName, setFullName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Ensure document direction is set correctly for RTL languages
  useDocumentDirection();

  // Update fullName when profile loads
  useEffect(() => {
    if (profile?.full_name) {
      setFullName(profile.full_name);
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    try {
      await updateProfile({ full_name: fullName });
      alert(t('settings.profileUpdated'));
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert(t('settings.profileUpdateFailed'));
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('settings.fileTooLarge'));
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(t('settings.invalidFileType'));
      return;
    }

    try {
      setIsUploading(true);
      const imageUrl = await uploadProfilePicture(file);
      await updateProfile({ profile_picture_url: imageUrl });
      alert(t('settings.photoUpdated'));
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert(t('settings.photoUpdateFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-brand-dark shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-brand-beige hover:text-white text-sm mb-1"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
                {t('courses.backToCourses')}
              </Link>
              <h1 className="text-xl sm:text-2xl font-bold text-white">{t('settings.title')}</h1>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-brand-beige rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-6 text-brand-dark">{t('settings.profileInfo')}</h2>

          <div className="flex items-start gap-6 mb-6">
            <div className="relative">
              {profile?.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt={displayName}
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-brand-terracotta text-white flex items-center justify-center font-medium text-2xl">
                  {initials}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 disabled:opacity-50 border border-brand-beige"
              >
                <Camera className="w-4 h-4 text-brand-terracotta" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>

            <div className="flex-1">
              <p className="text-sm text-brand-dark mb-2">
                {t('settings.uploadPhoto')}
              </p>
              {isUploading && (
                <p className="text-sm text-brand-terracotta">{t('settings.uploading')}</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                {t('settings.email')}
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-3 py-2 border border-brand-dark/20 rounded-lg bg-white/50 text-brand-dark cursor-not-allowed"
              />
              <p className="text-xs text-brand-dark/70 mt-1">{t('settings.emailCannotChange')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                {t('settings.fullName')}
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('settings.fullNamePlaceholder')}
                className="w-full px-3 py-2 border border-brand-dark/20 rounded-lg bg-white focus:ring-2 focus:ring-brand-terracotta focus:border-brand-terracotta"
              />
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={isUpdating}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-terracotta text-white rounded-lg hover:bg-brand-terracotta-dark disabled:opacity-50 font-medium"
            >
              <Save className="w-4 h-4" />
              {isUpdating ? t('settings.saving') : t('settings.saveChanges')}
            </button>
          </div>
        </div>

        <div className="bg-brand-beige rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-brand-dark">{t('settings.yourProgress')}</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-brand-dark">{t('settings.coursesStarted')}</span>
              <span className="font-semibold text-brand-dark">{progressStats?.coursesStarted ?? 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-brand-dark">{t('settings.coursesCompleted')}</span>
              <span className="font-semibold text-brand-dark">{progressStats?.coursesCompleted ?? 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-brand-dark">{t('settings.videosWatched')}</span>
              <span className="font-semibold text-brand-dark">{progressStats?.materialsCompleted ?? 0}</span>
            </div>            
          </div>
        </div>
      </main>
    </div>
  );
}
